import strings from "resources/strings";
import moment from "moment";
import "moment/locale/es";

export function numberFormatter(num, digits) {
  var si = [
    { value: 1, symbol: "" },
    { value: 1e3, symbol: "k" },
    { value: 1e6, symbol: "M" },
    { value: 1e9, symbol: "G" },
    { value: 1e12, symbol: "T" },
    { value: 1e15, symbol: "P" },
    { value: 1e18, symbol: "E" },
  ];
  var rx = /\.0+$|(\.[0-9]*[1-9])0+$/;
  var i;
  for (i = si.length - 1; i > 0; i--) {
    if (num >= si[i].value) {
      break;
    }
  }
  return (num / si[i].value).toFixed(digits).replace(rx, "$1") + si[i].symbol;
}

export function getStripeError(stripe_error) {
  const { code, decline_code } = stripe_error;

  if (decline_code) {
    return `Card declined.\n${getStripeDeclineError(decline_code)}`;
  } else if (code) {
    return getStripeCodeError(code);
  }

  return strings.alertUnexpectedError;

  // switch (type) {
  //   case "api_connection_error":
  //   case "api_error":
  //   case "authentication_error":
  //   case "card_error":
  //   case "idempotency_error":
  //   case "invalid_request_error":
  //   case "rate_limit_error":
  //   UNDOCUMENTED
  //   case "validation_error":
  // }
}

function getStripeCodeError(code) {
  switch (code) {
    // The email address provided for the creation of a deferred account already has an account associated with it.
    // Use the OAuth flow to connect the existing account to your platform.
    case "account_already_exists":
      return strings.alertUnexpectedError;

    // The country of the business address provided does not match the country of the account.
    // Businesses must be located in the same country as the account.
    case "account_country_invalid_address":
      return strings.alertUnexpectedError;

    // The account ID provided as a value for the Stripe-Account header is invalid.
    // Check that your requests are specifying a valid account ID.
    case "account_invalid":
      return strings.alertUnexpectedError;

    // The bank account number provided is invalid (e.g., missing digits). Bank account information varies from country to country.
    // We recommend creating validations in your entry forms based on the bank account formats we provide.
    case "account_number_invalid":
      return "The bank account number provided is invalid. Verify and try again.";

    // This method for creating Alipay payments is not supported anymore.
    // Please upgrade your integration to use Sources instead.
    case "alipay_upgrade_required":
      return strings.alertUnexpectedError;

    // The specified amount is greater than the maximum amount allowed.
    // Use a lower amount and try again.
    case "amount_too_large":
      return strings.alertUnexpectedError;

    // The specified amount is less than the minimum amount allowed.
    // Use a higher amount and try again.
    case "amount_too_small":
      return strings.alertUnexpectedError;

    // The API key provided has expired.
    // Obtain your current API keys from the Dashboard and update your integration to use them.
    case "api_key_expired":
      return strings.alertUnexpectedError;

    // The payment requires authentication to proceed.
    // If your customer is off session, notify your customer to return to your application and complete the payment.
    // If you provided the error_on_requires_action parameter, then your customer should try another card that does not require authentication.
    case "authentication_required":
      return strings.alertUnexpectedError;

    // The transfer or payout could not be completed because the associated account does not have a sufficient balance available.
    // Create a new transfer or payout using an amount less than or equal to the account’s available balance.
    case "balance_insufficient":
      return strings.alertUnexpectedError;

    // The bank account provided can not be used to charge, either because it is not verified yet or it is not supported.
    case "bank_account_declined":
      return strings.alertUnexpectedError;

    // The bank account provided already exists on the specified Customer object.
    // If the bank account should also be attached to a different customer, include the correct customer ID when making the request again.
    case "bank_account_exists":
      return "The bank account provided already exists.";

    // The bank account provided cannot be used for payouts.
    // A different bank account must be used.
    case "bank_account_unusable":
      return "The bank account provided cannot be used for payouts. Try again with a different bank account.";

    // Your Connect platform is attempting to share an unverified bank account with a connected account.
    case "bank_account_unverified":
      return strings.alertUnexpectedError;

    // The bank account cannot be verified, either because the microdeposit amounts provided do not match the actual amounts, or because verification has failed too many times.
    case "bank_account_verification_failed":
      return "The bank account cannot be verified. Please verify and try again.";

    // This method for creating Bitcoin payments is not supported anymore.
    // Please upgrade your integration to use Sources instead.
    case "bitcoin_upgrade_required":
      return strings.alertUnexpectedError;

    // This card has been declined too many times. You can try to charge this card again after 24 hours.
    // We suggest reaching out to your customer to make sure they have entered all of their information correctly and that there are no issues with their card.
    case "card_decline_rate_limit_exceeded":
      return "This card has been declined too many times. You can try to use this card again after 24 hours or try again with a different payment method.";

    // The card has been declined.
    // When a card is declined, the error returned also includes the decline_code attribute with the reason why the card was declined.
    case "card_declined":
      return "The card has been declined for an unknown reason. Contact your card issuer for more information.";

    // The country code provided was invalid.
    case "country_code_invalid":
      return "The country code provided was invalid.";

    // The maximum number of PaymentMethods for this Customer has been reached.
    // Either detach some PaymentMethods from this Customer or proceed with a different Customer.
    case "customer_max_payment_methods":
      return "You have reached the maximum number of payment methods allowed.";

    // The email address is invalid (e.g., not properly formatted).
    // Check that the email address is properly formatted and only includes allowed characters.
    case "email_invalid":
      return "The email address is invalid. Please verify and try again.";

    // The card has expired. Check the expiration date or use a different card.
    case "expired_card":
      return "The card has expired. Check the expiration date or use a different card.";

    // The card’s address is incorrect. Check the card’s address or use a different card.
    case "incorrect_address":
      return "The card’s address is incorrect. Check the card’s address or use a different card.";

    // The card’s security code is incorrect. Check the card’s security code or use a different card.
    case "incorrect_cvc":
      return "The card’s security code is incorrect. Check the card’s security code or use a different card.";

    // The card number is incorrect. Check the card’s number or use a different card.
    case "incorrect_number":
      return "The card number is incorrect. Check the card’s number or use a different card.";

    // The card’s postal code is incorrect. Check the card’s postal code or use a different card.
    case "incorrect_zip":
      return "The card’s postal code is incorrect. Check the card’s postal code or use a different card.";

    // The card’s security code is invalid. Check the card’s security code or use a different card.
    case "invalid_cvc":
      return "The card’s security code is invalid. Check the card’s security code or use a different card.";

    // The card’s expiration month is incorrect. Check the expiration date or use a different card.
    case "invalid_expiry_month":
      return "The card’s expiration month is incorrect. Check the expiration date or use a different card.";

    // The card’s expiration year is incorrect. Check the expiration date or use a different card.
    case "invalid_expiry_year":
      return "The card’s expiration year is incorrect. Check the expiration date or use a different card.";

    // The card number is invalid. Check the card details or use a different card.
    case "invalid_number":
      return "The card number is invalid. Check the card details or use a different card.";

    // The postal code provided was incorrect.
    case "postal_code_invalid":
      return "";

    // An error occurred while processing the card. Try again later or with a different payment method.
    case "processing_error":
      return "An error occurred while processing the card. Try again later or with a different payment method.";

    // The bank routing number provided is invalid.
    case "routing_number_invalid":
      return "The bank routing number provided is invalid. Please verify and try again.";

    // The tax ID number provided is invalid (e.g., missing digits). Tax ID information varies from country to country, but must be at least nine digits.
    case "tax_id_invalid":
      return "The tax ID number provided is invalid. Please verify and try again.";

    // The URL provided is invalid.
    case "url_invalid":
      return "The URL provided is invalid. Please verify and try again.";

    // UNDOCUMENTED CODES

    // Your card number is incomplete.
    case "incomplete_number":
      return "Your card number is incomplete.";

    // Your card's expiration date is in the past
    case "invalid_expiry_month_past":
      return "Your card's expiration date is in the past.";

    // Your card's expiration year is in the past.
    case "invalid_expiry_year_past":
      return "Your card's expiration year is in the past.";

    // Your card's expiration date is incomplete.
    case "incomplete_expiry":
      return "Your card's expiration date is incomplete.";

    // Your card's security code is incomplete.
    case "incomplete_cvc":
      return "Your card's security code is incomplete.";

    // Your postal code is incomplete.
    case "incomplete_zip":
      return "Your postal code is incomplete.";

    default:
      return strings.alertUnexpectedError;
  }
}

function getStripeDeclineError(decline_code) {
  switch (decline_code) {
    // The card was declined as the transaction requires authentication.
    // The customer should try again and authenticate their card when prompted during the transaction.
    case "authentication_required":
      return "Transaction requires authentication. Try again and authenticate your card when prompted.";

    // The payment cannot be authorized.
    // The payment should be attempted again. If it still cannot be processed, the customer needs to contact their card issuer.
    case "approve_with_id":
      return "Payment cannot be authorized. Try again or contact your card issuer for more information.";

    // The card has been declined for an unknown reason.
    // The customer needs to contact their card issuer for more information.
    case "call_issuer":
      return "The card has been declined for an unknown reason. Contact your card issuer for more information.";

    // The card does not support this type of purchase.
    // The customer needs to contact their card issuer to make sure their card can be used to make this type of purchase.
    case "card_not_supported":
      return "The card does not support this type of purchase. Contact your card issuer for more information.";

    // The customer has exceeded the balance or credit limit available on their card.
    // The customer should contact their card issuer for more information.
    case "card_velocity_exceeded":
      return "Card has exceeded the balance or credit limit available. Contact your card issuer for more information.";

    // The card does not support the specified currency.
    // The customer needs to check with the issuer whether the card can be used for the type of currency specified.
    case "currency_not_supported":
      return "The card does not support the specified currency. Contact your card issuer for more information.";

    // The card has been declined for an unknown reason.
    // The customer needs to contact their card issuer for more information.
    case "do_not_honor":
      return "The card has been declined for an unknown reason. Contact your card issuer for more information.";

    // 	The card has been declined for an unknown reason.
    // The customer should contact their card issuer for more information.
    case "do_not_try_again":
      return "The card has been declined for an unknown reason. Contact your card issuer for more information.";

    // A transaction with identical amount and credit card information was submitted very recently.
    // Check to see if a recent payment already exists.
    case "duplicate_transaction":
      return "A transaction with identical amount and credit card information was submitted very recently. Check to see if a recent payment already exists.";

    // The card has expired.
    // The customer should use another card.
    case "expired_card":
      return "The card has expired. Please update your payment method.";

    // The payment has been declined as Stripe suspects it is fraudulent.
    // Do not report more detailed information to your customer. Instead, present as you would the generic_decline described below.
    case "fraudulent":
      return "The card has been declined for an unknown reason. Contact your card issuer for more information.";

    // The card has been declined for an unknown reason.
    // The customer needs to contact their card issuer for more information.
    case "generic_decline":
      return "The card has been declined for an unknown reason. Contact your card issuer for more information.";

    // The card number is incorrect.
    // The customer should try again using the correct card number.
    case "incorrect_number":
      return "The card number is incorrect. Please verify and update your payment method.";

    // The CVC number is incorrect.
    // The customer should try again using the correct CVC.
    case "incorrect_cvc":
      return "The card CVC number is incorrect. Please verify and update your payment method.";

    // The PIN entered is incorrect. This decline code only applies to payments made with a card reader.
    // The customer should try again using the correct PIN.
    case "incorrect_pin":
      return "The PIN entered is incorrect. Please try again using the correct PIN.";

    // The ZIP/postal code is incorrect.
    // The customer should try again using the correct billing ZIP/postal code.
    case "incorrect_zip":
      return "The ZIP/postal code is incorrect. Please verify and update your payment method.";

    // The card has insufficient funds to complete the purchase.
    // The customer should use an alternative payment method.
    case "insufficient_funds":
      return "The card has insufficient funds to complete the purchase. Please verify and update your payment method.";

    // The card, or account the card is connected to, is invalid.
    // The customer needs to contact their card issuer to check that the card is working correctly.
    case "invalid_account":
      return "The card, or account the card is connected to, is invalid. Contact your card issuer for more information.";

    // The payment amount is invalid, or exceeds the amount that is allowed.
    // If the amount appears to be correct, the customer needs to check with their card issuer that they can make purchases of that amount.
    case "invalid_amount":
      return "The payment amount is invalid, or exceeds the amount that is allowed. Contact your card issuer for more information.";

    // The CVC number is incorrect.
    // The customer should try again using the correct CVC.
    case "invalid_cvc":
      return "The card CVC number is incorrect. Please verify and update your payment method.";

    // The expiration month is invalid.
    // The customer should try again using the correct expiration date.
    case "invalid_expiry_month":
      return "The expiration month is invalid. Please verify and update your payment method.";

    // The expiration year is invalid.
    // The customer should try again using the correct expiration date.
    case "invalid_expiry_year":
      return "The expiration year is invalid. Please verify and update your payment method.";

    // The card number is incorrect.
    // The customer should try again using the correct card number.
    case "invalid_number":
      return "The card number is incorrect. Please verify and update your payment method.";

    // The PIN entered is incorrect. This decline code only applies to payments made with a card reader.
    // The customer should try again using the correct PIN.
    case "invalid_pin":
      return "The PIN entered is incorrect. Please try again using the correct PIN.";

    // The card issuer could not be reached, so the payment could not be authorized.
    // The payment should be attempted again. If it still cannot be processed, the customer needs to contact their card issuer.
    case "issuer_not_available":
      return "The card issuer could not be reached. Try again or contact your card issuer for more information.";

    // The payment has been declined because the card is reported lost.
    // The specific reason for the decline should not be reported to the customer. Instead, it needs to be presented as a generic decline.
    case "lost_card":
      return "The card has been declined for an unknown reason. Contact your card issuer for more information.";

    // The payment has been declined because it matches a value on the Stripe user’s block list.
    // Do not report more detailed information to your customer. Instead, present as you would the generic_decline described above.
    case "merchant_blacklist":
      return "The card has been declined for an unknown reason. Contact your card issuer for more information.";

    // The card, or account the card is connected to, is invalid.
    // The customer needs to contact their card issuer for more information.
    case "new_account_information_available":
      return "The card, or account the card is connected to, is invalid. Contact your card issuer for more information.";

    // The card has been declined for an unknown reason.
    // The customer should contact their card issuer for more information.
    case "no_action_taken":
      return "The card has been declined for an unknown reason. Contact your card issuer for more information.";

    // The payment is not permitted.
    // The customer needs to contact their card issuer for more information.
    case "not_permitted":
      return "The payment is not permitted. Contact your card issuer for more information.";

    // The card has been declined as it requires a PIN.
    // The customer should try again by inserting their card and entering a PIN.
    case "offline_pin_required":
      return "The card has been declined as it requires a PIN. Try again by inserting the card and entering a PIN.";

    // The card has been declined as it requires a PIN.
    // If the card reader supports Online PIN, the customer should be prompted for a PIN without a new transaction being created.
    // If the card reader does not support Online PIN, the customer should try again by inserting their card and entering a PIN.
    case "online_or_offline_pin_required":
      return "The card has been declined as it requires a PIN. Try again or contact your card issuer for more information.";

    // The card cannot be used to make this payment (it is possible it has been reported lost or stolen).
    // The customer needs to contact their card issuer for more information.
    case "pickup_card":
      return "The card cannot be used to make this payment. Contact your card issuer for more information.";

    // The allowable number of PIN tries has been exceeded.
    // The customer must use another card or method of payment.
    case "pin_try_exceeded":
      return "The allowable number of PIN tries has been exceeded. Please update your payment method.";

    // An error occurred while processing the card.
    // The payment should be attempted again. If it still cannot be processed, try again later.
    case "processing_error":
      return "An error occurred while processing the card. Try again later.";

    // The payment could not be processed by the issuer for an unknown reason.
    // The payment should be attempted again. If it still cannot be processed, the customer needs to contact their card issuer.
    case "reenter_transaction":
      return "The card has been declined for an unknown reason. Try again or contact your card issuer for more information.";

    // The card cannot be used to make this payment (it is possible it has been reported lost or stolen).
    // The customer needs to contact their card issuer for more information.
    case "restricted_card":
      return "The card has been declined for an unknown reason. Contact your card issuer for more information.";

    // The card has been declined for an unknown reason.
    // The customer should contact their card issuer for more information.
    case "revocation_of_all_authorizations":
      return "The card has been declined for an unknown reason. Contact your card issuer for more information.";

    // The card has been declined for an unknown reason.
    // The customer should contact their card issuer for more information.
    case "revocation_of_authorization":
      return "The card has been declined for an unknown reason. Contact your card issuer for more information.";

    // The card has been declined for an unknown reason.
    // The customer needs to contact their card issuer for more information.
    case "security_violation":
      return "The card has been declined for an unknown reason. Contact your card issuer for more information.";

    // The card has been declined for an unknown reason.
    // The customer should contact their card issuer for more information.
    case "service_not_allowed":
      return "The card has been declined for an unknown reason. Contact your card issuer for more information.";

    // The payment has been declined because the card is reported stolen.
    // The specific reason for the decline should not be reported to the customer. Instead, it needs to be presented as a generic decline.
    case "stolen_card":
      return "The card has been declined for an unknown reason. Contact your card issuer for more information.";

    // The card has been declined for an unknown reason.
    // The customer should contact their card issuer for more information.
    case "stop_payment_order":
      return "The card has been declined for an unknown reason. Contact your card issuer for more information.";

    // A Stripe test card number was used.
    // A genuine card must be used to make a payment.
    case "testmode_decline":
      return "The card number is incorrect. Please verify and update your payment method.";

    // The card has been declined for an unknown reason.
    // The customer needs to contact their card issuer for more information.
    case "transaction_not_allowed":
      return "The card has been declined for an unknown reason. Contact your card issuer for more information.";

    // The card has been declined for an unknown reason.
    // Ask the customer to attempt the payment again. If subsequent payments are declined, the customer should contact their card issuer for more information.
    case "try_again_later":
      return "The card has been declined for an unknown reason. Try again or contact your card issuer for more information.";

    // The customer has exceeded the balance or credit limit available on their card.
    // The customer should use an alternative payment method.
    case "withdrawal_count_limit_exceeded":
      return "Card has exceeded the balance or credit limit available. Contact your card issuer for more information.";

    default:
      return "The card has been declined for an unknown reason. Try again or contact your card issuer for more information.";
  }
}

export function getFormattedUnixDate(unix_date, format = "datetime") {
  let date_format = "D [de] MMMM [de] YYYY[,] h[:]mm[:]ss A";
  if (format === "date") {
    date_format = "D [de] MMMM [de] YYYY";
  }

  return moment.unix(unix_date).locale("es").format(date_format);
}

export function getDateDifference(unix_date) {
  return moment.unix(unix_date).fromNow();
}

export function getNowUnix() {
  return moment().unix();
}

export function getUnixFromDate(date) {
  return moment(date).unix();
}
